import { HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { forwardRef, Inject, Injectable } from '@angular/core';
import { from, Observable, of, switchMap, tap } from 'rxjs';
import { USER_ACTION_V2 } from '../common/endpoints';
import { LOCALLY_HOSTED } from '../common/setup';
import { IndexedDBService } from './indexeddb.service';


@Injectable()
export class LocalNavigationInterceptor implements HttpInterceptor {

    constructor(@Inject(forwardRef(() => IndexedDBService)) private indexedDbService: IndexedDBService) { }

    private customRequests = [USER_ACTION_V2];

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let authReq = req;
        if (!LOCALLY_HOSTED) {
            return next.handle(authReq);
        }
        if (req.method != 'GET') {
            return next.handle(authReq);
        }
        if (window.navigator.onLine) {
            return next.handle(req).pipe(
                tap((event) => {
                    if (event instanceof HttpResponse) {
                        this.saveInStore(req, event);
                    }
                })
            );
        } else {
            return from(this.getFromStore(req)).pipe(
                switchMap(dbObject => of(new HttpResponse({ body: dbObject, status: 200 })))
            );
        }
    }

    private saveInStore(req: HttpRequest<any>, event: HttpResponse<any>): void {
        if (req.url.endsWith(USER_ACTION_V2)) {
            const actions = event.body?.content;
            if (actions) {
                const active = req.params.get('active') === 'true';
                const pageIndex = Number(req.params.get('page'));
                if (active) {
                    const thingId = req.params.get('thingId');
                    const workOrderParam = req.params.get('workOrderId');
                    const workOrderId = workOrderParam?.split(';')[1];
                    const idx = workOrderId ? 'page_thingId_workOrderId_idx' : 'page_thingId_idx';
                    const key = workOrderId ? [pageIndex, thingId, workOrderId] : [pageIndex, thingId];
                    this.indexedDbService.deleteByIndex('action', idx, key).then(() => {
                        actions.forEach(a => {
                            a.page = pageIndex;
                            this.doSaveWithRetry(this.indexedDbService.saveResource('action', a))
                        });
                    });
                } else { // historical (no deletion needed)
                    actions.forEach(a => this.doSaveWithRetry(this.indexedDbService.saveResource('historicalAction', a)));
                }

            }
        } else {
            this.doSaveWithRetry(this.indexedDbService.save(req.urlWithParams, event.body));
        }
    }

    private doSaveWithRetry(saveRequest: Promise<void>): void {
        saveRequest.catch(err => {
            if (err == IndexedDBService.DB_NOT_REFRENCED_ERROR) {
                this.indexedDbService.init().then(() => saveRequest).catch(err => console.error(err));
            } else {
                console.error(err);
            }
        });
    }

    private getFromStore(req: HttpRequest<any>): Promise<any> {
        if (req.url.endsWith(USER_ACTION_V2)) {
            return this.getActions(req.params);
        } else {
            return this.indexedDbService.get(req.urlWithParams);
        }
    }

    private getActions(params: HttpParams): Promise<any> {
        const pageIndex = Number(params.get('page'));
        const pageSize = Number(params.get('size'));
        const active = params.get('active') === 'true';
        const workOrderParam = params.get('workOrderId');
        const workOrderId = workOrderParam?.split(';')[1] || null;
        return this.indexedDbService.getActionsPaginated(pageIndex, pageSize, 'desc', active, params.get('thingId'), workOrderId);
    }

}

export const localNavigationInterceptorProviders = [
    { provide: HTTP_INTERCEPTORS, useClass: LocalNavigationInterceptor, multi: true }
];